import React from "react";
import { Minus, Plus } from "lucide-react";

/**
 * Accessible quantity stepper. Controlled component.
 * onChange(newQty). Clamps to [min, max].
 *
 * Sizes: `sm` is bumped to 44×44 buttons because mobile basket rows commonly
 * use the small variant — Apple HIG minimum is 44px and the previous 32px
 * targets were tap-fail prone with thumbs.
 */
export default function QtyStepper({
  value,
  onChange,
  min = 1,
  max = 10,
  testIdPrefix = "qty",
  size = "md",
}) {
  const sizes = {
    sm: { btn: "h-11 w-11", input: "h-11 w-11 text-base" },
    md: { btn: "h-11 w-11", input: "h-11 w-12 text-base" },
  };
  const s = sizes[size] || sizes.md;

  const dec = () => onChange(Math.max(min, value - 1));
  const inc = () => onChange(Math.min(max, value + 1));

  return (
    <div
      className="inline-flex items-center rounded-full border border-navy/80 overflow-hidden select-none"
      data-testid={`${testIdPrefix}-stepper`}
    >
      <button
        type="button"
        onClick={dec}
        disabled={value <= min}
        aria-label="Decrease quantity"
        className={`${s.btn} inline-flex items-center justify-center text-navy hover:bg-ice active:bg-ice/80 disabled:opacity-40 disabled:hover:bg-transparent transition-colors`}
        data-testid={`${testIdPrefix}-decrement`}
      >
        <Minus size={16} strokeWidth={2.2} />
      </button>
      <span
        aria-live="polite"
        aria-atomic="true"
        className={`${s.input} inline-flex items-center justify-center font-semibold text-navy tabular-nums border-x border-navy/15`}
        data-testid={`${testIdPrefix}-value`}
      >
        {value}
      </span>
      <button
        type="button"
        onClick={inc}
        disabled={value >= max}
        aria-label="Increase quantity"
        className={`${s.btn} inline-flex items-center justify-center text-navy hover:bg-ice active:bg-ice/80 disabled:opacity-40 disabled:hover:bg-transparent transition-colors`}
        data-testid={`${testIdPrefix}-increment`}
      >
        <Plus size={16} strokeWidth={2.2} />
      </button>
    </div>
  );
}
